import { useId, type ReactNode } from 'react';
import { cx } from '../cx';
import { Icon } from '../Icon';
import styles from './Field.module.css';

// Field — the label / control / hint / error frame shared by Input, Textarea and Select (DESIGN_SYSTEM §12
// rows 2–4). The label is a real <label for>, hint and error are wired through `aria-describedby`, and an
// error also sets `aria-invalid` on the control. Errors say how to fix it ("Meta needs at least $1.00 per day").

export interface FieldProps {
  label: ReactNode;
  /** Adds "(optional)" after the label. */
  optional?: boolean;
  hint?: ReactNode;
  /** Present = the control is invalid. Say what to do, not what went wrong. */
  error?: ReactNode;
  /** Id of the control; generated when omitted. */
  id?: string;
  className?: string;
  testID?: string;
  children: (control: FieldControlProps) => ReactNode;
}

/** What the field hands to its control. */
export interface FieldControlProps {
  id: string;
  'aria-describedby': string | undefined;
  'aria-invalid': true | undefined;
}

export function Field({
  label,
  optional,
  hint,
  error,
  id,
  className,
  testID,
  children,
}: FieldProps) {
  const generated = useId();
  const controlId = id ?? `field-${generated}`;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cx(styles.field, className)} data-testid={testID}>
      <label className={styles.label} htmlFor={controlId}>
        {label}
        {optional ? <span className={styles.optional}>(optional)</span> : null}
      </label>
      {children({
        id: controlId,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
      })}
      {hint ? (
        <span className={styles.hint} id={hintId}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className={styles.error} id={errorId}>
          <Icon name="alert-circle" size={14} />
          {error}
        </span>
      ) : null}
    </div>
  );
}

export const fieldStyles = styles;
